import type { Dayjs, OpUnitType } from "dayjs";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import duration, { DurationUnitType } from "dayjs/plugin/duration";
import dayOfYear from "dayjs/plugin/dayOfYear";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import weekOfYear from "dayjs/plugin/weekOfYear";
import { TimeUnit } from "pages/[orgId]/explore/lib/FormattingControls";
import { SearchState } from "src/search/types";
import { formatWithSpecificUnits } from "./duration";

dayjs.extend(relativeTime);
dayjs.extend(duration);
dayjs.extend(quarterOfYear);
dayjs.extend(weekOfYear);
dayjs.extend(dayOfYear);

// takes a duration in milliseconds and returns a human readable string
export type DurationType = "millis" | "seconds" | "minutes";

/**
 * @deprecated Use formatDuration from duration.ts instead
 */
export const durationReadable = (
  durationInMillis: number,
  durationType: DurationType = "millis"
) => {
  if (durationType === "minutes") {
    durationInMillis = durationInMillis * 60 * 1000;
  }
  if (durationType === "seconds") {
    durationInMillis = durationInMillis * 1000;
  }
  const seconds = Math.floor((durationInMillis / 1000) % 60);
  const minutes = Math.floor((durationInMillis / (1000 * 60)) % 60);
  const hours = Math.floor((durationInMillis / (1000 * 60 * 60)) % 24);
  const days = Math.floor(durationInMillis / (1000 * 60 * 60 * 24));

  const daysStr = days ? `${days}d ` : "";
  const hoursStr = hours ? `${hours}h ` : "";
  const minutesStr = minutes ? `${minutes}m ` : "";
  const secondsStr = seconds ? `${seconds}s` : "";

  return `${daysStr}${hoursStr}${minutesStr}${secondsStr}`;
};

export type DateParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  timeZoneOffsetSeconds: number;
};

export function isDateParts(value?: unknown | DateParts): value is DateParts {
  if (!value) {
    return false;
  }
  const date = value as DateParts;
  return (
    isNumber(date.year) &&
    isNumber(date.month) &&
    isMaybeNumber(date.day) &&
    isMaybeNumber(date.hour) &&
    isMaybeNumber(date.minute) &&
    isMaybeNumber(date.second) &&
    isMaybeNumber(date.timeZoneOffsetSeconds)
  );
}

function isNumber(val: unknown | number): val is number {
  return typeof val === "number";
}

function isMaybeNumber(val: unknown | number): val is number | undefined {
  return val === undefined || typeof val === "number";
}

export const dateToString = (value?: string | DateParts) => {
  if (!value) {
    return null;
  }
  if (isDateParts(value)) {
    return toDate(value).toISOString();
  }
  if (isStringDate(value)) {
    return value;
  }
  return null;
};

// yes, urls can successfully parse as dates.
export function isStringDate(value: unknown | string): value is string {
  return (
    typeof value === "string" &&
    !value.startsWith("http") &&
    !Number.isNaN(Date.parse(value))
  );
}

// This method always assumes the date is in UTC.
export const toDate = (value: DateParts) => {
  const utcDate = dayjs.utc(
    Date.UTC(
      value.year,
      value.month - 1,
      value.day,
      value.hour,
      value.minute,
      value.second,
      value.timeZoneOffsetSeconds
    )
  );
  //convert to local timezone
  return utcDate.local().toDate();
};

export const formatTime = (
  time: string,
  smartOrFormat: boolean | string = true,
  options?: Partial<{
    smartFormat: string;
    smartUnit: OpUnitType;
    smartDuration: number;
  }> // needs refactored, but I don't wanna right now.
) => {
  const smartUnit: OpUnitType = options?.smartUnit || "days";
  const smartDuration = options?.smartDuration || 5;
  const smartFormat = options?.smartFormat || "MMMM D, YYYY h:mm A";
  const date = dayjs(time);

  const now = dayjs();
  const duration = now.diff(date, smartUnit);

  return smartOrFormat === duration < smartDuration
    ? date.fromNow() // Show relative time for dates within 5 days
    : date.format(
        typeof smartOrFormat === "string" ? smartOrFormat : smartFormat
      ); // Show absolute time for dates longer than 5 days
};

export function idealTimeUnit(
  start: Dayjs,
  end: Dayjs
): "days" | "hours" | "minutes" {
  const daysDelta = Math.abs(start.diff(end, "days"));
  if (daysDelta > 5) return "days";
  const hoursDelta = Math.abs(start.diff(end, "hours"));
  if (hoursDelta > 6) return "hours";
  return "minutes";
}

export const timeframeToString = (
  timeframeOverride:
    | Pick<SearchState, "duration" | "startDate" | "endDate">
    | undefined
) => {
  if (!timeframeOverride) return undefined;
  const { duration, startDate, endDate } = timeframeOverride;
  if (duration) {
    return RELATIVE_PRESETS.find((p) => p.value === duration)?.label || "";
  }
  if (startDate && endDate) {
    return `${formatTime(startDate, "YYYY-MM-DD")} - ${formatTime(
      endDate,
      "YYYY-MM-DD"
    )}`;
  }
  return undefined;
};

export const RELATIVE_PRESETS = [
  { label: "Last 24 Hours", value: "P1D" },
  { label: "Last 48 Hours", value: "P2D" },
  { label: "Last 3 Days", value: "P3D" },
  { label: "Yesterday", value: "L1D" },
  { label: "This week", value: "T1W" },
  { label: "Last 7 Days", value: "P7D" },
  { label: "Last Week", value: "L1W" },
  { label: "Last 14 Days", value: "P14D" },
  { label: "Last 2 Weeks", value: "L2W" },
  { label: "This month", value: "T1M" },
  { label: "Last 30 Days", value: "P30D" },
  { label: "Last month", value: "L1M" },
  { label: "Last 3 Months", value: "P3M" },
  { label: "Last 6 Months", value: "P6M" },
  { label: "Last 12 Months", value: "P1Y" },
  { label: "This year", value: "T1Y" },
] as const;
export type Duration = (typeof RELATIVE_PRESETS)[number]["value"];

export const PREVIOUS_PRESETS = RELATIVE_PRESETS.filter((p) =>
  p.value.startsWith("P")
).map((p) => ({
  label: p.label.replace("Past", "Previous"),
  value: p.value,
  shortLabel: p.value.replace("P", "-"),
}));

export function isDuration(value: string): value is Duration {
  return Boolean(RELATIVE_PRESETS.some((preset) => preset.value === value));
}

export function previousDuration(duration: Duration): {
  startDate: string;
  endDate: string;
} {
  const days = daysInDuration(duration);
  const endDate = dayjs().subtract(days, "days");
  const startDate = endDate.subtract(days, "days");
  return {
    startDate: startDate.startOf("day").toISOString(),
    endDate: endDate.endOf("day").toISOString(),
  };
}

export function durationToRange(duration: Duration): {
  startDate: string;
  endDate: string;
} {
  const days = daysInDuration(duration);
  const endDate = dayjs();
  const startDate = endDate.subtract(days, "days");
  return {
    startDate: startDate.startOf("day").toISOString(),
    endDate: endDate.endOf("day").toISOString(),
  };
}

export function previousRange(
  start: string,
  end: string
): {
  startDate: string;
  endDate: string;
} {
  const days = daysInRange(start, end);
  const endDate = dayjs(start);
  const startDate = endDate.add(days, "days");
  return {
    startDate: startDate.startOf("day").toISOString(),
    endDate: endDate.endOf("day").toISOString(),
  };
}

export function nextRange(
  start: string,
  end: string
): {
  startDate: string;
  endDate: string;
} {
  const days = daysInRange(start, end);
  const potentialStartDate = dayjs(end);
  const startDate = potentialStartDate.isSame(dayjs(), "day")
    ? dayjs().subtract(1, "day")
    : potentialStartDate;
  const potentialEndDate = startDate.subtract(days, "days");
  const endDate = potentialEndDate.isAfter(dayjs())
    ? dayjs()
    : potentialEndDate;
  return {
    startDate: startDate.startOf("day").toISOString(),
    endDate: endDate.endOf("day").toISOString(),
  };
}

function daysInDuration(duration: Duration): number {
  // if (duration === "P1D") return 1; // Using as the base case..
  if (duration === "P2D") return 2;
  if (duration === "P3D") return 3;
  if (duration === "P7D") return 7;
  if (duration === "P14D") return 14;
  if (duration === "P30D") return 30;
  if (duration === "P3M") return 90;
  if (duration === "P6M") return 180;
  if (duration === "P1Y") return 365;
  if (duration === "L1W") return 7;
  if (duration === "L1M") return 30; // could be better
  if (duration === "T1W") return 7;
  if (duration === "T1M") return 30; // could be better
  return 1;
}

function daysInRange(start: string, end: string): number {
  return dayjs(start).diff(end, "days");
}

const durationSuffixes = {
  years: "y",
  months: "mo",
  days: "d",
  hours: "h",
  minutes: "min",
  seconds: "s",
} as const;

export const formatCalendarValue = (
  value: number | string,
  unit: string
): string => {
  if (unit == "yearmonthday") {
    // 34/12/01
    return `${value}`.split("/").slice(1).join("/");
  } else if (unit == "yearweek") {
    //23/45
    const [year, week] = `${value}`.split("/");
    const fullYear = Number(year) + 2000; // Convert 2-digit year to 4-digit year
    const startOfWeek = dayjs()
      .year(fullYear)
      .startOf("year")
      .week(Number(week))
      .startOf("week");
    return `${startOfWeek.format("MM/DD/YY")}`;
  } else if (unit == "yearmonth") {
    const [year, month] = `${value}`.split("/");
    const stringMonth = formatDatePart(Number(month), "month");
    return `${stringMonth} '${year}`;
  } else if (unit == "yearquarter") {
    const [year, quarter] = `${value}`.split("/");
    const stringQuarter = formatDatePart(Number(quarter), "quarter");
    return `${stringQuarter} '${year}`;
  }

  return value + "";
};

export const formatDatePart = (value: number, formatSource: string): string => {
  const now = dayjs();
  switch (formatSource) {
    case "date": {
      return now.date(value).format("Do");
    }
    case "day": {
      return now.day(value).format("dddd");
    }
    case "hour": {
      return now.hour(value).format("ha");
    }
    case "minute": {
      return now.minute(value).format("mm");
    }
    case "month": {
      return now.month(value - 1).format("MMM");
    }
    case "quarter": {
      return "Q" + now.quarter(value).format("Q");
    }
    case "week": {
      return now.week(value).format("wo");
    }
    case "year": {
      return now.year(value).format("YYYY");
    }
    default: {
      return `${value}-${formatSource}`;
    }
  }
};
